import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sparkline — the smallest honest chart.
 *
 * A headline number with nothing to compare it against carries no information:
 * "$5.7B" cannot say whether that is a lot for this agency, or rising. This is
 * the trend that goes under it — no axes, no grid, no legend, just the shape
 * and an emphasised endpoint.
 *
 * Hand-drawn SVG rather than recharts: at 120x28 a charting library's margins,
 * tooltips and resize observer cost more than the mark is worth, and the whole
 * point is that it stays cheap enough to put on every stat tile.
 */
import { useId } from 'react';
export const Sparkline = ({ values, width = 120, height = 28, color = 'var(--accent, var(--color-primary, #3b82f6))', fillOpacity = 0.14, label, className, }) => {
    const gradientId = useId();
    const clean = (values || []).filter((v) => Number.isFinite(v));
    if (clean.length < 2)
        return null;
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    // A flat series has zero span; without this the whole line collapses onto
    // the top edge (divide by zero) instead of sitting sensibly in the middle.
    const span = max - min || 1;
    const pad = 2;
    const stepX = (width - pad * 2) / (clean.length - 1);
    const y = (v) => pad + (1 - (v - min) / span) * (height - pad * 2);
    const points = clean.map((v, i) => [pad + i * stepX, y(v)]);
    const line = points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
    const area = `${pad},${height} ${line} ${(width - pad).toFixed(1)},${height}`;
    const [lastX, lastY] = points[points.length - 1];
    return (_jsxs("svg", { width: width, height: height, viewBox: `0 0 ${width} ${height}`, className: `${className || ''} chsvg chsvg--overflow`, role: label ? 'img' : 'presentation', "aria-label": label, "aria-hidden": label ? undefined : true, children: [_jsx("defs", { children: _jsxs("linearGradient", { id: gradientId, x1: "0", y1: "0", x2: "0", y2: "1", children: [_jsx("stop", { offset: "0%", stopColor: color, stopOpacity: fillOpacity }), _jsx("stop", { offset: "100%", stopColor: color, stopOpacity: 0 })] }) }), _jsx("polygon", { points: area, fill: `url(#${gradientId})` }), _jsx("polyline", { points: line, fill: "none", stroke: color, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: lastX, cy: lastY, r: 2.5, fill: color })] }));
};
export default Sparkline;
